import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  Text,
} from '@flatfile/baseline'
import { KeyboardEvent, MouseEvent, ReactNode, useState } from 'react'

interface RenameDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  currentName: string
  onRename: (newName: string) => Promise<void>
  trigger: ReactNode
  title: string
  className?: string
}

export const RenameDialog = ({
  isOpen,
  onOpenChange,
  currentName,
  onRename,
  trigger,
  title,
  className,
}: RenameDialogProps) => {
  const [newName, setNewName] = useState(currentName)
  const [isRenamingLoading, setIsRenamingLoading] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)

  const handleRenameSubmit = async () => {
    if (!newName || newName === currentName) {
      onOpenChange(false)
      return
    }

    setRenameError(null)
    setIsRenamingLoading(true)

    try {
      await onRename(newName)
      onOpenChange(false)
    } catch (error) {
      setRenameError('Failed to rename. Please try again later.')
      console.error('Failed to rename:', error)
    } finally {
      setIsRenamingLoading(false)
    }
  }

  return (
    <DropdownMenu open={isOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        sideOffset={5}
        side='bottom'
        alignOffset={0}
        className='z-[1001] w-[270px] p-0'
      >
        <div className='p-3'>
          <div className='mb-2'>
            <Text className='text-sm font-medium'>{title}*</Text>
          </div>
          <Input
            type='text'
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleRenameSubmit()
              }
            }}
            onClick={(e: MouseEvent<HTMLInputElement>) => e.stopPropagation()}
            className='w-full'
            size='small'
            autoFocus
          />
          {renameError && (
            <div className='text-destructive text-xs pt-2'>{renameError}</div>
          )}
          <div className='flex justify-start gap-2 pt-3'>
            <Button
              variant='outline'
              size='sm'
              className='theme-static'
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              size='sm'
              loading={isRenamingLoading}
              className='theme-static'
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                handleRenameSubmit()
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
